import axios from 'axios';

// Create a dedicated client for AI to avoid baseURL conflicts
const aiClient = axios.create({
  baseURL: '/ai-service-api',
  headers: { 'Content-Type': 'application/json' },
});

aiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export interface AIChatMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  metadata?: any;
  timestamp?: string;
}

export interface AIChartSeries {
  key: string;
  label: string;
  color?: string;
}

export interface AIChartSpec {
  type: 'bar' | 'line' | 'area' | 'pie';
  title: string;
  description?: string;
  xKey?: string;
  labelKey?: string;
  yKeys?: string[];
  series?: AIChartSeries[];
  data: Record<string, string | number | null>[];
}

export interface AIAskRequest {
  prompt: string;
  history?: AIChatMessage[];
  vendor?: 'gemini' | 'anthropic' | 'deepseek';
  model?: string;
  image?: {
    mimeType: string;
    dataBase64: string;
  };
}

export interface AIAskResponse {
  response: string;
  metadata: {
    vendor: string;
    model: string;
    latencyMs: number;
    usage?: any;
    toolCalls?: any[];
    chart?: AIChartSpec | null;
    context?: {
      vendor: 'gemini' | 'anthropic' | 'deepseek';
      model: string;
      systemInstruction: string;
      historyUsed: Array<{ role: string; content: string }>;
      memoriesUsed: string[];
      sqlQueries: string[];
      toolCalls: any[];
    };
  };
  userMessage?: AIChatMessage;
  assistantMessage?: AIChatMessage;
}

export interface AIModelSetting {
  vendor: 'gemini' | 'anthropic' | 'deepseek';
  display_name: string;
  default_model: string;
  is_active: boolean;
  updated_at?: string;
}

export interface AIAdminConfigResponse {
  models: AIModelSetting[];
}

export interface SendAiAttachmentMailPayload {
  email: string;
  filename: string;
  title: string;
  contentBase64: string;
  contentType?: string;
  prompt?: string;
  vendor?: string;
  model?: string;
}

export const aiApi = {
  ask: async (data: AIAskRequest): Promise<AIAskResponse> => {
    const response = await aiClient.post<AIAskResponse>('/ask', data);
    return response.data;
  },
  getHistory: async (): Promise<AIChatMessage[]> => {
    const response = await aiClient.get<AIChatMessage[]>('/history');
    return response.data;
  },
  searchHistory: async (query: string): Promise<AIChatMessage[]> => {
    const response = await aiClient.get<AIChatMessage[]>(`/history/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },
  clearHistory: async (): Promise<{ status: string; message: string }> => {
    const response = await aiClient.post<{ status: string; message: string }>('/history/clear');
    return response.data;
  },
  deleteHistoryItem: async (id: string): Promise<{ status: string }> => {
    const response = await aiClient.delete<{ status: string }>(`/history/${id}`);
    return response.data;
  },
  getStats: async (): Promise<any> => {
    const response = await aiClient.get('/stats');
    return response.data;
  },
  getUsageHistory: async (limit: number = 50): Promise<any[]> => {
    const response = await aiClient.get(`/usage-history?limit=${limit}`);
    return response.data;
  },
  getAdminConfig: async (): Promise<AIAdminConfigResponse> => {
    const response = await aiClient.get<AIAdminConfigResponse>('/config');
    return response.data;
  },
  updateModelSettings: async (settings: Array<Pick<AIModelSetting, 'vendor' | 'is_active' | 'default_model'>>): Promise<AIAdminConfigResponse> => {
    const response = await aiClient.put<AIAdminConfigResponse>('/config/models', { settings });
    return response.data;
  },
  getInstructions: async (): Promise<string> => {
    const response = await aiClient.get<{ instructions: string }>('/config/instructions');
    return response.data.instructions;
  },
  updateInstructions: async (instructions: string): Promise<void> => {
    await aiClient.put('/config/instructions', { instructions });
  },
  sendAttachmentByEmail: async (payload: SendAiAttachmentMailPayload): Promise<void> => {
    await axios.post('/api/reports/send-ai-pdf', payload, {
      headers: {
        'Content-Type': 'application/json',
        ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}),
      },
    });
  },
  sendPdfByEmail: async (payload: SendAiAttachmentMailPayload): Promise<void> => {
    await aiApi.sendAttachmentByEmail({
      ...payload,
      contentType: payload.contentType || 'application/pdf',
    });
  },
};
